import { useState } from 'react'
import { useNavigate } from 'react-router'
import { Button, InfoBox } from '@biznomi/ui-react'
import { api } from '@/lib/api/client'
import { useAuth } from '@/lib/auth/store'
import { useOrg } from '@/lib/useOrg'

/**
 * 개인정보 수집·이용 동의.
 *
 * ⚠️ 아래 고지 문구는 법적 고지다. apps/client 의 것을 그대로 옮겼다.
 *    보기 좋게 다듬지 말 것 — 수집 항목·목적·보관 기간이 실제와 어긋나면
 *    그건 UI 문제가 아니라 법적 문제다. 바꾸려면 §개인정보 보관기간 폐기 절차와
 *    같이 검토해야 한다 (docs/2026-07-28_개인정보-보관기간-폐기-절차.md).
 *
 * 🔴 **문구가 실제와 어긋나 있다** (2026-09-09 실측 —
 *    docs/2026-09-09_개인정보-수집항목-실측.md). 서류 3종(계약서·급여명세서·교육
 *    이수증)·손글씨 서명·휴가 사유·감사 로그가 고지에 없다. DCMC 문구를 옮겨 왔는데
 *    DCMC 는 출퇴근만 쓰기 때문이다. 고치려면 노무사·변호사 확인이 필요해서
 *    **문구는 그대로 두었다** — 여기서 그럴듯하게 새로 쓰면 또 하나의 틀린 고지가 된다.
 *
 * ✅ 다만 «누가 수집하나» 는 문구 결정과 무관해서 먼저 채웠다. 아래 §조직명 참조.
 */

const SECTIONS = [
  { title: '수집 항목', items: ['이름, 사번 (이메일은 이메일 로그인 사용 시에만 수집)', '출퇴근 시각 기록', '기기 정보 (기기명, OS, 기기 고유 ID)'] },
  { title: '이용 목적', items: ['근태 관리 서비스 제공', '출퇴근 기록 관리', '기기 인증 및 보안'] },
  { title: '보관 기간', items: ['재직 기간 및 퇴직 후 3년', '법령에 따른 보존 의무가 있는 경우 해당 기간'] },
]

export default function Privacy() {
  const navigate = useNavigate()
  const user = useAuth((s) => s.user)
  const setAuth = useAuth((s) => s.setAuth)
  const { accessToken, refreshToken } = useAuth()

  const { data: org } = useOrg()
  const [checked, setChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  async function agree() {
    setBusy(true)
    setError('')
    // 서버는 consented:true 를 요구한다 (onboarding.dto.ts PrivacyConsentSchema).
    // 빈 body 면 400 이다 — 체크박스를 켰다는 사실이 body 에 실려야 한다.
    const res = await api<{ message: string }>('/api/auth/privacy-consent', {
      method: 'POST',
      body: JSON.stringify({ consented: true }),
    })
    setBusy(false)

    if (!res.success) {
      setError(res.error ?? '동의 처리에 실패했습니다')
      return
    }

    // 서버가 갱신했으니 로컬 세션도 맞춰준다 — 안 그러면 게이트가 계속 여기로 돌린다
    if (user && accessToken && refreshToken) {
      setAuth({ accessToken, refreshToken }, { ...user, privacyConsentedAt: new Date().toISOString() })
    }
    navigate('/onboarding/device', { replace: true })
  }

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold">개인정보 수집 및 이용 동의</h1>

      {/*
        🔴 «누가 수집하나» 를 적는다 (2026-09-09).

        개인정보처리자는 **직원을 고용한 회사**이고 메디펀은 수탁자다
        (docs/2026-07-28_개인정보-보관기간-폐기-절차.md §1 — 「메디펀은 수탁사(처리자),
        대가대는 위탁자(관리자)」). 그런데 이 화면에는 회사 이름도 위탁 관계도 없었다.

        DCMC 는 회사가 하나라 안 적어도 통했다. **여러 회사가 쓰는 제품에서는
        «누가 수집하나» 가 비어 있는 것이다.**

        ⚠ 조직명이 아직 안 왔으면 그 줄을 안 그린다. 「undefined 이(가) 수집합니다」 보다
          아무 말도 안 하는 쪽이 낫다.
      */}
      {org?.name && (
        <p className="text-sm leading-relaxed text-slate-600">
          <b>{org.name}</b>이(가) 아래와 같이 개인정보를 수집·이용합니다.
          <br />
          시스템 운영과 보관은 <b>주식회사 메디펀</b>(비즈노미)에 위탁합니다.
        </p>
      )}

      <div className="space-y-4 rounded-card bg-card p-5">
        {SECTIONS.map((s) => (
          <section key={s.title}>
            <h2 className="mb-1.5 text-sm font-bold">{s.title}</h2>
            <ul className="space-y-1">
              {s.items.map((i) => (
                <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-600">
                  <span className="text-slate-300">·</span>
                  <span>{i}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <InfoBox tone="warning">동의를 거부할 수 있으나, 거부 시 서비스 이용이 제한됩니다.</InfoBox>

      <label className="flex cursor-pointer items-start gap-3 rounded-card bg-card p-4">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="mt-0.5 size-5 shrink-0 accent-[var(--color-primary)]"
        />
        <span className="text-sm leading-relaxed">
          위 내용을 확인하였으며, 개인정보 수집 및 이용에 동의합니다.
        </span>
      </label>

      {error && (
        <p role="alert" className="rounded-lg bg-[var(--color-danger-bg)] px-3 py-2 text-sm text-[var(--color-danger)]">
          {error}
        </p>
      )}

      <Button size="lg" className="w-full" disabled={!checked} loading={busy} onClick={agree}>
        동의하고 시작하기
      </Button>
    </div>
  )
}
